/**
 * GuidanceDefStorage — manages local storage of GuidanceDef content records.
 *
 * GuidanceDef content arrives as a ContentType='GuidanceDef' record from the backend.
 * The content_body column contains base64-encoded JSON that matches the C# GuidanceDefDto
 * structure (PascalCase on the wire).
 *
 * Multiple GuidanceDefs may be stored (e.g. one per language or source),
 * each identified by its SourceId.
 *
 * Storage structure:
 *   Key:   guidance_def_{sourceId}   e.g. guidance_def_standard_en
 *   Value: JSON-serialised GuidanceDefDto (camelCase)
 *   Index: guidance_def_index        array of stored sourceIds
 *
 * Wire format (base64 → JSON, PascalCase):
 * {
 *   "SourceId": "standard_en",
 *   "Name": "Standard Guidance",
 *   "Desc": "...",
 *   "Footer": "...",
 *   "LanguageTypeId": "1",
 *   "AgeGroups": [
 *     {
 *       "Name": "0-12 Months",
 *       "Desc": "...",
 *       "Days": "365",
 *       "Domains": [
 *         {
 *           "Name": "Communication",
 *           "Items": [
 *             { "MVar": "guid_comm_001", "Desc": "Talk to your baby..." }
 *           ]
 *         }
 *       ]
 *     }
 *   ]
 * }
 */

import type { ContentRecord } from '../stores/contentStore';

// Re-use the same StorageInterface shape used throughout the codebase
export interface StorageInterface {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

// ─── DTO interfaces (camelCase, mirrors C# PascalCase) ───────────────────────

export interface GuidanceItemDto {
  /** Milestone variable name */
  mVar: string;
  /** Guidance description / tip */
  desc: string;
}

export interface GuidanceDomainDto {
  /** Domain display name */
  name: string;
  items: GuidanceItemDto[];
}

export interface GuidanceAgeGroupDto {
  /** Display name of the age group, e.g. "0-3 Months" */
  name: string;
  /** Optional age group description */
  desc: string;
  /** Maximum age in days that defines this group */
  days: string;
  domains: GuidanceDomainDto[];
}

export interface GuidanceDefDto {
  /** Unique identifier, e.g. "standard_en" */
  sourceId: string;
  /** Human-readable name */
  name: string;
  /** Short description */
  desc: string;
  /** Footer text shown at the bottom of guidance content */
  footer: string;
  languageTypeId: string;
  ageGroups: GuidanceAgeGroupDto[];
  /** ISO timestamp set when the record was stored locally */
  downloadedAt: string;
}

// ─── Storage class ────────────────────────────────────────────────────────────

export class GuidanceDefStorage {
  private readonly keyPrefix = 'guidance_def_';
  private readonly indexKey  = 'guidance_def_index';

  constructor(private storage: StorageInterface) {}

  // ─── Private helpers ────────────────────────────────────────────────────────

  private storageKey(sourceId: string): string {
    return `${this.keyPrefix}${sourceId}`;
  }

  private decodeBase64(b64: string): string {
    try {
      const bufferApi = (globalThis as any).Buffer;
      if (bufferApi?.from) {
        return bufferApi.from(b64, 'base64').toString('utf-8');
      }
      return atob(b64);
    } catch (err) {
      throw new Error(`GuidanceDefStorage: failed to decode base64 — ${err}`);
    }
  }

  private async addToIndex(sourceId: string): Promise<void> {
    try {
      const raw = await this.storage.getItem(this.indexKey);
      const ids: string[] = raw ? JSON.parse(raw) : [];
      if (!ids.includes(sourceId)) {
        ids.push(sourceId);
        await this.storage.setItem(this.indexKey, JSON.stringify(ids));
      }
    } catch (_) { /* non-fatal */ }
  }

  private async removeFromIndex(sourceId: string): Promise<void> {
    try {
      const raw = await this.storage.getItem(this.indexKey);
      const ids: string[] = raw ? JSON.parse(raw) : [];
      const updated = ids.filter(id => id !== sourceId);
      await this.storage.setItem(this.indexKey, JSON.stringify(updated));
    } catch (_) { /* non-fatal */ }
  }

  private async getIndex(): Promise<string[]> {
    try {
      const raw = await this.storage.getItem(this.indexKey);
      return raw ? JSON.parse(raw) : [];
    } catch (_) {
      return [];
    }
  }

  // ─── Public API ──────────────────────────────────────────────────────────────

  /**
   * Persist a GuidanceDefDto keyed by sourceId.
   */
  async saveGuidanceDef(dto: GuidanceDefDto): Promise<boolean> {
    try {
      const record: GuidanceDefDto = { ...dto, downloadedAt: dto.downloadedAt || new Date().toISOString() };
      const sourceId = record.sourceId || 'unknown';
      await this.storage.setItem(this.storageKey(sourceId), JSON.stringify(record));
      await this.addToIndex(sourceId);
      return true;
    } catch (err) {
      return false;
    }
  }

  /**
   * Retrieve a stored GuidanceDefDto by sourceId.
   * Falls back to the first available record when sourceId is omitted.
   */
  async getGuidanceDef(sourceId?: string): Promise<GuidanceDefDto | null> {
    try {
      if (sourceId) {
        const raw = await this.storage.getItem(this.storageKey(sourceId));
        return raw ? (JSON.parse(raw) as GuidanceDefDto) : null;
      }
      // Fallback: return first available
      const ids = await this.getIndex();
      if (ids.length === 0) return null;
      const raw = await this.storage.getItem(this.storageKey(ids[0]));
      return raw ? (JSON.parse(raw) as GuidanceDefDto) : null;
    } catch (err) {
      console.error('❌ GuidanceDefStorage.getGuidanceDef failed:', sourceId, err);
      return null;
    }
  }

  /**
   * Return all stored GuidanceDefDto records.
   */
  async getAllGuidanceDefs(): Promise<GuidanceDefDto[]> {
    const ids = await this.getIndex();
    const results: GuidanceDefDto[] = [];
    for (const id of ids) {
      const dto = await this.getGuidanceDef(id);
      if (dto) results.push(dto);
    }
    return results;
  }

  /**
   * Delete a GuidanceDefDto by sourceId.
   */
  async deleteGuidanceDef(sourceId: string): Promise<boolean> {
    try {
      await this.storage.removeItem(this.storageKey(sourceId));
      await this.removeFromIndex(sourceId);
      console.log(`🗑️ GuidanceDefStorage: deleted sourceId="${sourceId}"`);
      return true;
    } catch (err) {
      console.error('❌ GuidanceDefStorage.deleteGuidanceDef failed:', sourceId, err);
      return false;
    }
  }

  /**
   * Remove all stored GuidanceDef records and the index.
   */
  async clearAll(): Promise<void> {
    const ids = await this.getIndex();
    for (const id of ids) {
      try { await this.storage.removeItem(this.storageKey(id)); } catch (_) { /* continue */ }
    }
    try { await this.storage.removeItem(this.indexKey); } catch (_) { /* non-fatal */ }
    console.log('🗑️ GuidanceDefStorage: cleared all records');
  }

  // ─── Query helpers ────────────────────────────────────────────────────────────

  /**
   * Return the age group appropriate for the child's age in days.
   * Selects the first age group whose `days` value is >= ageInDays,
   * falling back to the last group when the child exceeds all defined ranges.
   * @param ageInDays  Child's chronological age in days
   * @param sourceId   Optional — defaults to first available def
   */
  async getAgeGroupForAge(ageInDays: number, sourceId?: string): Promise<GuidanceAgeGroupDto | null> {
    const dto = await this.getGuidanceDef(sourceId);
    if (!dto || dto.ageGroups.length === 0) return null;
    for (const group of dto.ageGroups) {
      const groupDays = parseInt(group.days, 10);
      if (isNaN(groupDays) || ageInDays <= groupDays) return group;
    }
    // Child exceeds all defined ranges — return the last group
    return dto.ageGroups[dto.ageGroups.length - 1];
  }

  /**
   * Return all items across all age groups and domains for a given developmental domain name
   * (case-insensitive match on `domain.name`).
   * @param domainName  e.g. "Communication"
   * @param sourceId    Optional — defaults to first available def
   */
  async getItemsForDomain(domainName: string, sourceId?: string): Promise<GuidanceItemDto[]> {
    const dto = await this.getGuidanceDef(sourceId);
    if (!dto) return [];
    const lower = domainName.toLowerCase();
    const results: GuidanceItemDto[] = [];
    for (const group of dto.ageGroups) {
      for (const domain of group.domains) {
        if (domain.name.toLowerCase() === lower) {
          results.push(...domain.items);
        }
      }
    }
    return results;
  }

  // ─── Content-record unpacking ─────────────────────────────────────────────────

  /**
   * Unpack a ContentStore record of type "GuidanceDef" and persist it.
   *
   * Expected record.tableData shape (standard content-server response):
   *   Columns: [..., "content_body", ...]
   *   Rows:    [[..., "<base64-encoded JSON>", ...]]
   *
   * The base64-encoded JSON payload must match the C# GuidanceDefDto structure.
   * Returns the stored dto on success, null on failure.
   */
  async unpackFromContentRecord(record: ContentRecord): Promise<GuidanceDefDto | null> {
    try {

      const tableData = (record as any).tableData;
      if (!tableData?.Data?.Columns || !tableData?.Data?.Rows?.length) {
        console.warn('⚠️ GuidanceDefStorage: ContentRecord has no tableData rows');
        return null;
      }

      const columns: string[] = tableData.Data.Columns;
      const bodyIdx = columns.findIndex((c: string) => c.toLowerCase() === 'content_body');
      if (bodyIdx === -1) {
        console.warn('⚠️ GuidanceDefStorage: content_body column not found. Available:', columns);
        return null;
      }

      const firstRow = tableData.Data.Rows[0];
      const encodedBody: string = firstRow[bodyIdx];
      if (!encodedBody) {
        console.warn('⚠️ GuidanceDefStorage: content_body is empty');
        return null;
      }

      const jsonString = this.decodeBase64(encodedBody);
      const p = JSON.parse(jsonString); // p = parsed PascalCase object from C# backend

      // ── Map PascalCase → camelCase ──────────────────────────────────────────

      const mapItems = (arr: any[]): GuidanceItemDto[] =>
        (arr ?? []).map((i: any): GuidanceItemDto => ({
          mVar: i.MVar ?? i.mVar ?? '',
          desc: i.Desc ?? i.desc ?? '',
        }));

      const mapDomains = (arr: any[]): GuidanceDomainDto[] =>
        (arr ?? []).map((d: any): GuidanceDomainDto => ({
          name:  d.Name  ?? d.name  ?? '',
          items: mapItems(d.Items ?? d.items ?? []),
        }));

      const ageGroups: GuidanceAgeGroupDto[] = (p.AgeGroups ?? p.ageGroups ?? []).map((g: any): GuidanceAgeGroupDto => ({
        name:    g.Name    ?? g.name    ?? '',
        desc:    g.Desc    ?? g.desc    ?? '',
        days:    g.Days    ?? g.days    ?? '0',
        domains: mapDomains(g.Domains ?? g.domains ?? []),
      }));

      const dto: GuidanceDefDto = {
        sourceId:      p.SourceId      ?? p.sourceId      ?? 'unknown',
        name:          p.Name          ?? p.name          ?? '',
        desc:          p.Desc          ?? p.desc          ?? '',
        footer:        p.Footer        ?? p.footer        ?? '',
        languageTypeId: p.LanguageTypeId ?? p.languageTypeId ?? '1',
        ageGroups,
        downloadedAt:  new Date().toISOString(),
      };

      const totalItems = ageGroups.reduce((sum, g) =>
        sum + g.domains.reduce((ds, d) => ds + d.items.length, 0), 0);

      const saved = await this.saveGuidanceDef(dto);
      if (!saved) return null;

      return dto;
    } catch (err) {
      return null;
    }
  }
}
